import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Text,
  Flex,
  IconButton,
  Icon,
  HStack,
  Box,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  Checkbox,
  Spinner,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FaBackward, FaPause, FaPlay, FaRedo } from 'react-icons/fa';
import { getTeamInfoById } from '../utils/getTeamInfo';
import apiClient from '../lib/api/apiClient';
import dayjs from 'dayjs';
import BroadcastGame, {
  type BroadcastGameHandle,
  type BroadcastGameRow,
  type BroadcastPlaybackSpeed,
} from './BroadcastGame';

export type { BroadcastGameRow };

interface GameDetailedResponse {
  game?: Record<string, unknown> | null;
  sections: Array<{ title: string; columns: string[]; rows: Record<string, unknown>[] }>;
}

interface BroadcastGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: number | null;
  gameRow: BroadcastGameRow | null;
  onGameNotFound?: () => void;
  league: { conferences?: Array<{ divisions?: Array<{ teams?: Array<{ id: number; name?: string; logo?: string; color_primary?: string; color_secondary?: string; color_text?: string; abbreviation?: string; rw_abbreviation?: string }> }> }> } | null;
}

function teamAbbr(info: { abbreviation?: string; rw_abbreviation?: string; name?: string } | null): string {
  if (!info) return '—';
  const a = info.rw_abbreviation || info.abbreviation;
  if (a) return a.toUpperCase();
  const n = (info.name || '').trim();
  if (n.length <= 5) return n.toUpperCase();
  return n
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();
}

const BroadcastGameModal: React.FC<BroadcastGameModalProps> = ({
  isOpen,
  onClose,
  gameId,
  gameRow,
  onGameNotFound,
  league,
}) => {
  const modalBg = '#0a0d12';
  const SPEED_OPTIONS = [
    { label: '1/3x', value: 1 / 6 },
    { label: '2/3x', value: 1 / 3 },
    { label: '1x', value: 0.5 },
    { label: '2x', value: 1 },
    { label: '4x', value: 2 },
  ] as const;
  const DEFAULT_SPEED_INDEX = 2;
  const [isPaused, setIsPaused] = useState(false);
  const [speedIndex, setSpeedIndex] = useState(DEFAULT_SPEED_INDEX);
  const [feedOnly, setFeedOnly] = useState(false);
  const [replayNonce, setReplayNonce] = useState(0);
  const [playbackFinished, setPlaybackFinished] = useState(false);
  const playbackSpeed: BroadcastPlaybackSpeed = SPEED_OPTIONS[speedIndex]?.value ?? SPEED_OPTIONS[DEFAULT_SPEED_INDEX].value;

  const onPlaybackComplete = useCallback(() => {
    setPlaybackFinished(true);
  }, []);

  const onRewind = useCallback(() => {
    setPlaybackFinished(false);
  }, []);

  const broadcastRef = useRef<BroadcastGameHandle>(null);
  const hasReportedNotFoundRef = useRef(false);

  useEffect(() => {
    // Reset so a new deep-link/gameId can re-trigger the defensive flow.
    if (!isOpen) {
      hasReportedNotFoundRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsPaused(false);
      setPlaybackFinished(false);
      setSpeedIndex(DEFAULT_SPEED_INDEX);
      setFeedOnly(false);
      setReplayNonce(0);
    }
  }, [isOpen]);

  const { data: detailedData, isLoading: detailedLoading } = useQuery<GameDetailedResponse>({
    queryKey: ['gameDetailed', gameId],
    queryFn: async () => {
      const res = await apiClient.get(`/games/scores/game/${gameId}/detailed`);
      return res.data;
    },
    enabled: isOpen && !!gameId,
  });

  const playByPlaySection = detailedData?.sections?.find((s) => s.title === 'PlayByPlay');
  const playByPlayRows = playByPlaySection?.rows ?? [];
  const announcementKey = playByPlaySection?.columns?.includes('announcement') ? 'announcement' : 'play_text';

  useEffect(() => {
    if (!isOpen) return;
    if (!gameId) return;
    if (detailedLoading) return;
    // If the API returns `game: null`, the requested game doesn't exist in DB.
    if (detailedData && detailedData.game == null && !hasReportedNotFoundRef.current) {
      hasReportedNotFoundRef.current = true;
      onGameNotFound?.();
    }
  }, [isOpen, gameId, detailedLoading, detailedData, onGameNotFound]);

  // Build a working `gameRow` from the detailed payload when the page didn't provide it
  // (e.g. deep-linking to games older than the current scoreboard date range).
  const resolvedGameRow: BroadcastGameRow | null = (() => {
    if (gameRow) return gameRow;
    const gameObj = detailedData?.game as any | undefined;
    if (!gameObj) return null;
    return {
      game_id: Number(gameObj.id ?? gameObj.game_id ?? gameId ?? 0),
      scheduled_date: String(gameObj.scheduled_date ?? ''),
      away_team_id: Number(gameObj.away_team_id ?? 0),
      home_team_id: Number(gameObj.home_team_id ?? 0),
      away_runs: Number(gameObj.away_runs ?? 0),
      home_runs: Number(gameObj.home_runs ?? 0),
      innings: Number(gameObj.innings ?? 9),
    };
  })();

  if (!league) return null;

  const awayInfo =
    resolvedGameRow?.away_team_id != null
      ? getTeamInfoById(resolvedGameRow.away_team_id, league as Parameters<typeof getTeamInfoById>[1])
      : null;
  const homeInfo =
    resolvedGameRow?.home_team_id != null
      ? getTeamInfoById(resolvedGameRow.home_team_id, league as Parameters<typeof getTeamInfoById>[1])
      : null;

  const awayName =
    awayInfo?.name ?? (resolvedGameRow ? `Team ${resolvedGameRow.away_team_id}` : '—');
  const homeName =
    homeInfo?.name ?? (resolvedGameRow ? `Team ${resolvedGameRow.home_team_id}` : '—');

  const dateLabel = resolvedGameRow?.scheduled_date ? dayjs(resolvedGameRow.scheduled_date).format('MMM D, YYYY') : '—';
  const controlsDisabled = detailedLoading || playByPlayRows.length === 0 || !resolvedGameRow;

  const handleReplay = () => {
    setPlaybackFinished(false);
    setIsPaused(false);
    setReplayNonce((n) => n + 1);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent
        w={{ base: '100vw', md: '75vw' }}
        maxH={{ base: '100dvh', md: '85vh' }}
        h={{ base: '100dvh', md: '85vh' }}
        m={0}
        bg={modalBg}
        borderRadius={{ base: 0, md: 'md' }}
        borderWidth="1px"
        borderColor="whiteAlpha.200"
      >
        <ModalCloseButton zIndex={20} color="gray.400" _hover={{ color: 'white', bg: 'whiteAlpha.200' }} />
        <ModalBody p={0} display="flex" flexDir="column" minH={0} position="relative" pl={{ base: 5, md: 8 }} pr={{ base: 5, md: 8 }}>
          <Flex align="center" mb={3} px={1} pt={1} flexShrink={0} w="100%" gap={2} minH="44px">
            <Box flex="1" minW={0}>
              <Text display={{ base: 'none', md: 'block' }} fontSize="sm" color="gray.300" textAlign="left">
                Game Date: {dateLabel}
              </Text>
            </Box>

            <Flex flex="1" justify="center" minW={0}>
              <HStack
                spacing={3}
                px={3}
                py={2}
                borderRadius="md"
                borderWidth="1px"
                borderColor="whiteAlpha.200"
                bg="blackAlpha.500"
              >
                <IconButton
                  aria-label="Rewind to previous half inning"
                  icon={<Icon as={FaBackward} />}
                  size="sm"
                  variant="ghost"
                  color="gray.300"
                  _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                  isDisabled={controlsDisabled}
                  onClick={() => broadcastRef.current?.rewindToPreviousHalf()}
                />
                {playbackFinished ? (
                  <IconButton
                    aria-label="Replay broadcast from the beginning"
                    icon={<Icon as={FaRedo} />}
                    size="sm"
                    variant="ghost"
                    color="gray.300"
                    _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                    isDisabled={controlsDisabled}
                    onClick={handleReplay}
                  />
                ) : (
                  <IconButton
                    aria-label={isPaused ? 'Resume broadcast' : 'Pause broadcast'}
                    icon={<Icon as={isPaused ? FaPlay : FaPause} />}
                    size="sm"
                    variant="ghost"
                    color="gray.300"
                    _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                    isDisabled={controlsDisabled}
                    onClick={() => setIsPaused((p) => !p)}
                  />
                )}
                <VStack spacing={1.5} align="stretch" minW="150px">
                  <Slider
                    aria-label="Broadcast speed"
                    min={0}
                    max={SPEED_OPTIONS.length - 1}
                    step={1}
                    value={speedIndex}
                    onChange={(v) => setSpeedIndex(v)}
                    isDisabled={controlsDisabled}
                  >
                    <SliderTrack bg="whiteAlpha.300">
                      <SliderFilledTrack bg="teal.300" />
                    </SliderTrack>
                    <SliderThumb boxSize={3.5} />
                  </Slider>
                  <Text fontSize="xs" color="gray.300" textAlign="center">
                    Playback Speed
                  </Text>
                </VStack>
                <Checkbox
                  size="sm"
                  colorScheme="teal"
                  pl={2}
                  isChecked={feedOnly}
                  onChange={(e) => setFeedOnly(e.target.checked)}
                  isDisabled={controlsDisabled}
                  color="gray.300"
                  whiteSpace="nowrap"
                >
                  Feed Only
                </Checkbox>
              </HStack>
            </Flex>

            <Box flex="1" />
          </Flex>

          <Flex flex={1} minH={0} direction="column" px={1} pb={1}>
            {resolvedGameRow ? (
              <BroadcastGame
                ref={broadcastRef}
                key={`${resolvedGameRow.game_id}-${replayNonce}`}
                isActive={isOpen}
                isLoading={detailedLoading}
                isPaused={isPaused}
                playbackSpeed={playbackSpeed}
                replayNonce={replayNonce}
                onPlaybackComplete={onPlaybackComplete}
                onRewind={onRewind}
                playbackComplete={playbackFinished}
                feedOnly={feedOnly}
                playByPlayRows={playByPlayRows}
                announcementKey={announcementKey}
                gameRow={resolvedGameRow}
                awayTeam={{
                  name: awayName,
                  abbr: teamAbbr(awayInfo),
                  logo: awayInfo?.logo,
                  color_primary: awayInfo?.color_primary,
                  color_secondary: awayInfo?.color_secondary,
                  color_text: awayInfo?.color_text,
                }}
                homeTeam={{
                  name: homeName,
                  abbr: teamAbbr(homeInfo),
                  logo: homeInfo?.logo,
                  color_primary: homeInfo?.color_primary,
                  color_secondary: homeInfo?.color_secondary,
                  color_text: homeInfo?.color_text,
                }}
              />
            ) : (
              <Flex justify="center" align="center" minH="220px">
                <Spinner size="lg" color="teal.400" />
              </Flex>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BroadcastGameModal;
